import { CalendarDays, SlidersHorizontal } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { api, formatApiError } from "@/lib/api";


const today = new Date().toISOString().slice(0, 10);

const formatCurrency = (value) => new Intl.NumberFormat("id-ID", {
  style: "currency",
  currency: "IDR",
  maximumFractionDigits: 0,
}).format(value || 0);

const formatDate = (value) => new Intl.DateTimeFormat("id-ID", {
  day: "2-digit",
  month: "short",
  year: "numeric",
}).format(new Date(`${value}T00:00:00`));

export const ManagerProfitReport = () => {
  const [selectedDate, setSelectedDate] = useState(today);
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const loadReport = useCallback(async (dateValue) => {
    setIsLoading(true);
    setError("");
    try {
      const response = await api.get("/reports/profit", {
        params: { date_from: dateValue, date_to: dateValue },
      });
      setReport(response.data);
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReport(today);
  }, [loadReport]);

  const applyFilter = (event) => {
    event.preventDefault();
    loadReport(selectedDate);
    setIsFilterOpen(false);
  };

  const isComplete = Boolean(report?.is_complete);

  return (
    <main className="workspace manager-reports-page" data-testid="manager-reports-page">
      <section className="page-heading manager-report-heading">
        <div>
          <p className="eyebrow">LAPORAN CABANG</p>
          <h1 data-testid="manager-reports-title">Laba harian</h1>
        </div>
        <button
          className="compact-filter-button"
          data-testid="manager-profit-filter-toggle"
          onClick={() => setIsFilterOpen((current) => !current)}
          type="button"
        >
          <SlidersHorizontal aria-hidden="true" size={16} />
          Filter
        </button>
      </section>

      {isFilterOpen && (
        <form className="manager-filter-popover" data-testid="manager-profit-filter-form" onSubmit={applyFilter}>
          <label htmlFor="manager-profit-date">Tanggal laporan</label>
          <input
            data-testid="manager-profit-date-input"
            id="manager-profit-date"
            onChange={(event) => setSelectedDate(event.target.value)}
            required
            type="date"
            value={selectedDate}
          />
          <button data-testid="manager-profit-filter-submit" type="submit">Terapkan</button>
        </form>
      )}

      {error && <p className="form-error" data-testid="manager-reports-error" role="alert">{error}</p>}
      {isLoading && <p className="loading-note" data-testid="manager-reports-loading">Membaca laba POS…</p>}

      <section className="manager-profit-table-section" aria-labelledby="manager-profit-table-title">
        <div className="manager-report-table-heading">
          <div>
            <CalendarDays aria-hidden="true" size={18} />
            <h2 id="manager-profit-table-title">Laporan laba</h2>
          </div>
          <span data-testid="manager-profit-report-status">
            {isComplete ? "DATA LENGKAP" : "MENUNGGU DATA LABA"}
          </span>
        </div>
        <div className="manager-profit-table" data-testid="manager-profit-table">
          <div className="manager-profit-table-labels">
            <span>Tanggal</span>
            <span>Penjualan</span>
            <span>HPP / Modal</span>
            <span>Laba</span>
          </div>
          <div className="manager-profit-table-row" data-testid="manager-profit-table-row">
            <strong data-label="Tanggal">{formatDate(selectedDate)}</strong>
            <span data-label="Penjualan">{isComplete ? formatCurrency(report.total_sales) : "—"}</span>
            <span data-label="HPP / Modal">{isComplete ? formatCurrency(report.total_hpp) : "—"}</span>
            <strong className="profit-positive" data-label="Laba">
              {isComplete ? `+ ${formatCurrency(report.total_profit)}` : "Menunggu data"}
            </strong>
          </div>
        </div>
        <p className="manager-profit-caption" data-testid="manager-profit-caption">
          Laba belum termasuk biaya operasional.
        </p>
      </section>
    </main>
  );
};